import { TiffProcessingStatus } from './../../predictor/models/TiffProcessingStatus';
import { ConfigurationProfile } from './../../predictor/interfaces/configuration-profile';
import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { PredictorModelActions } from './predictor-model.actions';
import { TerrainInspectDetails } from 'src/app/predictor/models/TerrainInspectDetails';
import { EMPTY, asapScheduler, catchError, map, mergeMap, of, tap } from 'rxjs';
import { FileUploadService } from 'src/app/predictor/services/file-upload.service';
import { TerrainDataService } from 'src/app/predictor/services/terrain-data.service';
import { HttpEventType } from '@angular/common/http';
import { toastyReport } from 'src/app/global/operators/ngxs-rxjs-toast-report';
import { InteractionType } from 'src/app/predictor/enums/interaction-type';
import { append, patch, removeItem } from '@ngxs/store/operators';
import { CanvasLayer } from 'src/app/global/interfaces/canvas-layer';
import { AgentSettings } from 'src/app/predictor/interfaces/agent-settings';
import { toasty } from 'src/app/global/operators/ngxs-rxjs-toast';
import { CanvasService } from 'src/app/predictor/services/canvas.service';
import { StudyArea } from 'src/app/predictor/interfaces/study-area';
import { IGeometryGeoJSON } from 'src/app/predictor/interfaces/geo';
import { TerrainLoadingStatus } from 'src/app/predictor/enums/terrain-loading-status';
import { SimulationService } from 'src/app/predictor/services/simulation.service';
import { TerrainRenderingStatus } from '../../predictor/enums/terrain-rendering-status';
import { AzFunctionRuntimeStatus } from '../../predictor/enums/az-function-runtime-status';
import { NotificationsActions } from '../notifications/notifications.actions';
import { Severity } from '../../global/enums/severity';
import { ConfigurationProfileService } from '../../predictor/services/configuration-profile.service';
import { StatusMessage } from '../../predictor/interfaces/status-message';
import { StudyAreaStatusService } from '../../predictor/services/study-area-status.service';
import { SimulationLoopService } from 'src/app/predictor/services/simulation-loop.service';

export interface SelectedStudyAreaModel {
  name: string;
  id: string;
  shape: IGeometryGeoJSON | null;
  simulationId: string | null;
  terrainId: string | null;
  status?: TerrainLoadingStatus;
  error: string | null;
  licenseNumber: string | null; // Optional field for license number
}

export interface PredictorModelStateModel {
  /** show inspect detail panel */
  showDetails: boolean;

  /** show the canvas */
  showCanvas: boolean;

  /** users study areas list */
  studyAreas: StudyArea[];

  /** details of selected study area */
  selectedStudyAreaData: SelectedStudyAreaModel | null;

  /** current terrain state used for debugging*/
  terrainState: string;

  /** terrain data rendering status */
  terrainRenderingStatus: TerrainRenderingStatus;

  exportingResults: boolean;
  shapeFiles: CanvasLayer[];
  selectedShapefile?: CanvasLayer;
  selectedSimulationId?: string;
  studyAreaAgentSettings?: AgentSettings;
  inspectDetails: TerrainInspectDetails;
  fishnetShapeFileName: string;
  demFileUploading: boolean;
  demFileUploadStatus: number;
  terrainLoadingStatus: TerrainLoadingStatus;
  interactionType: InteractionType;
  currentAgentSettings: AgentSettings;
  configurationProfiles: ConfigurationProfile[];
  scenarios: CanvasLayer[];
  showStudyAreaDropDown?: boolean;
}

const stateDefaults: PredictorModelStateModel = {
  terrainState: '',
  showDetails: false,
  showCanvas: false,
  terrainLoadingStatus: TerrainLoadingStatus.None,
  terrainRenderingStatus: TerrainRenderingStatus.None,
  studyAreas: [],
  selectedStudyAreaData: null,
  shapeFiles: [],
  selectedShapefile: undefined,
  selectedSimulationId: undefined,
  fishnetShapeFileName: '',
  exportingResults: false,
  demFileUploading: false,
  demFileUploadStatus: 0,
  inspectDetails: {
    imageXy: 'Choose Point',
    northing: 0,
    easting: 0,
    elevation: 0,
    debrisDepth: 0,
    erosion: 0,
    deposition: 0,
    slope: 0,
    d8Aspect: '-',
    dInfinitAspect: 0,
    landslide: 0,
    utmX: 0,
    utmY: 0,
    eventCount: 0,
    minDepth: 0,
    maxDepth: 0,
  },
  interactionType: InteractionType.None,
  configurationProfiles: [],
  currentAgentSettings: {
    fanMaxSlope: 27,
    fanMinΣ: 0.35,
    fanMaxΣ: 1.15,
    fanExponent: 1,
    maxSpawns: 100,
    depositionMultiplier: 1,
    erosionMultiplier: 1,
    lossPercent: 0,
    minInitDepth: 1,
    removeRandomness: false,
    showAgents: false,
  },
  scenarios: [],
};

@State<PredictorModelStateModel>({
  name: 'PredictorModel',
  defaults: stateDefaults,
})
@Injectable()
export class PredictorModelState {
  constructor(
    private fileUploadService: FileUploadService,
    private terrainDataService: TerrainDataService,
    private configurationProfileService: ConfigurationProfileService,
    private simulationService: SimulationService,
    private canvasService: CanvasService,
    private simulationLoopService: SimulationLoopService
  ) {}

  @Selector()
  static isExportingResults(
    predictorModelStateModel: PredictorModelStateModel
  ): boolean {
    return predictorModelStateModel.exportingResults;
  }

  @Action(PredictorModelActions.SetExporting)
  setExporting(
    ctx: StateContext<PredictorModelStateModel>,
    { isExporting }: PredictorModelActions.SetExporting
  ) {
    ctx.patchState({
      exportingResults: isExporting,
    });
  }

  @Selector()
  static studyAreas(
    predictorModelStateModel: PredictorModelStateModel
  ): StudyArea[] {
    return predictorModelStateModel.studyAreas;
  }

  /**
   * @returns Status Message of the study area steps. Terrain loading, DEM file uploading, etc.
   */
  @Selector()
  static studyAreaStatusMessage(
    state: PredictorModelStateModel
  ): StatusMessage | null {
    return StudyAreaStatusService.getStatusMessage(
      state.terrainRenderingStatus,
      state.selectedStudyAreaData,
      state.demFileUploading,
      state.demFileUploadStatus,
      state.terrainLoadingStatus
    );
  }

  @Selector()
  static groupedStudyAreas(predictorModelStateModel: PredictorModelStateModel) {
    return [
      {
        label: 'Playground Study Areas',
        items: predictorModelStateModel.studyAreas.filter(
          (studyarea) => studyarea.global
        ),
      },
      {
        label: 'Purchased Study Areas',
        items: predictorModelStateModel.studyAreas.filter(
          (studyarea) => !studyarea.global
        ),
      },
    ];
  }

  @Selector()
  static selectedStudyArea(
    predictorModelStateModel: PredictorModelStateModel
  ): StudyArea | undefined {
    return predictorModelStateModel.studyAreas.find(
      (studyArea) =>
        studyArea.studyAreaName ==
        predictorModelStateModel.selectedStudyAreaData?.name
    );
  }

  @Selector()
  static selectedStudyAreaHasTerrain(
    predictorModelStateModel: PredictorModelStateModel
  ): boolean {
    const studyArea: StudyArea | undefined =
      predictorModelStateModel.studyAreas.find(
        (studyArea) =>
          studyArea.studyAreaName ==
          predictorModelStateModel.selectedStudyAreaData?.name
      );

    return !!studyArea?.terrainId;
  }

  @Selector()
  static agentSettings(
    predictorModelStateModel: PredictorModelStateModel
  ): AgentSettings {
    return predictorModelStateModel.currentAgentSettings;
  }

  @Selector()
  static demFileUploadStatus(
    predictorModelStateModel: PredictorModelStateModel
  ): number {
    return predictorModelStateModel.demFileUploadStatus;
  }

  @Selector()
  static terrainRenderingStatus(
    predictorModelStateModel: PredictorModelStateModel
  ): TerrainRenderingStatus {
    return predictorModelStateModel.terrainRenderingStatus;
  }

  @Selector()
  static terrainRendered(
    predictorModelStateModel: PredictorModelStateModel
  ): boolean {
    return (
      predictorModelStateModel.terrainRenderingStatus ===
      TerrainRenderingStatus.Rendered
    );
  }

  /** show the three.js detail panel */
  @Selector()
  static showDetails(
    predictorModelStateModel: PredictorModelStateModel
  ): boolean {
    return predictorModelStateModel.showDetails;
  }

  /** show the map three.js control panel */
  @Selector()
  static showControls(
    predictorModelStateModel: PredictorModelStateModel
  ): boolean {
    return !!predictorModelStateModel.selectedSimulationId;
  }

  @Selector()
  static showCanvas(
    predictorModelStateModel: PredictorModelStateModel
  ): boolean {
    return predictorModelStateModel.showCanvas;
  }

  @Selector()
  static terrainLoadingStatus(
    predictorModelStateModel: PredictorModelStateModel
  ): TerrainLoadingStatus {
    return predictorModelStateModel.terrainLoadingStatus;
  }

  @Selector()
  static terrainLoaded(
    predictorModelStateModel: PredictorModelStateModel
  ): boolean {
    return (
      predictorModelStateModel.terrainLoadingStatus ===
      TerrainLoadingStatus.Done
    );
  }

  @Selector()
  static selectedStudyAreaData(
    predictorModelStateModel: PredictorModelStateModel
  ) {
    return predictorModelStateModel.selectedStudyAreaData;
  }

  @Selector()
  static selectedShapeFile(
    predictorModelStateModel: PredictorModelStateModel
  ): CanvasLayer | undefined {
    return predictorModelStateModel.selectedShapefile;
  }

  @Selector()
  static shapeFiles(
    predictorModelStateModel: PredictorModelStateModel
  ): CanvasLayer[] {
    return predictorModelStateModel.shapeFiles;
  }

  @Selector()
  static fishnetShapeFileName(
    predictorModelStateModel: PredictorModelStateModel
  ): string {
    return predictorModelStateModel.fishnetShapeFileName;
  }

  @Selector()
  static scenarios(
    predictorModelStateModel: PredictorModelStateModel
  ): CanvasLayer[] {
    return predictorModelStateModel.scenarios;
  }

  @Selector()
  static visiblePointScenarios(
    predictorModelStateModel: PredictorModelStateModel
  ): CanvasLayer[] {
    return predictorModelStateModel.scenarios.filter(
      (scenario: CanvasLayer) => scenario.type === 'Point' && scenario.visible
    );
  }

  @Selector()
  static selectedSimulationId(
    predictorModelStateModel: PredictorModelStateModel
  ): string | undefined {
    return predictorModelStateModel.selectedSimulationId;
  }

  @Selector()
  static selectedTerrainId(
    predictorModelStateModel: PredictorModelStateModel
  ): string | undefined {
    return (
      predictorModelStateModel.selectedStudyAreaData?.terrainId || undefined
    );
  }

  @Selector()
  static studyAreaAgentSettings(
    predictorModelStateModel: PredictorModelStateModel
  ): AgentSettings | undefined {
    return predictorModelStateModel.studyAreaAgentSettings;
  }

  @Selector()
  static demFileLoading(
    predictorModelStateModel: PredictorModelStateModel
  ): boolean {
    return predictorModelStateModel.demFileUploading;
  }

  @Selector()
  static inspectDetails(
    predictorModelStateModel: PredictorModelStateModel
  ): TerrainInspectDetails {
    return predictorModelStateModel.inspectDetails;
  }

  @Selector()
  static interactionType(
    predictorModelStateModel: PredictorModelStateModel
  ): InteractionType {
    return predictorModelStateModel.interactionType;
  }

  @Selector()
  static openStudyAreaDropDown(
    predictorModelStateModel: PredictorModelStateModel
  ): boolean {
    return predictorModelStateModel.showStudyAreaDropDown === true;
  }

  @Selector()
  static configurationProfiles(
    predictorModelStateModel: PredictorModelStateModel
  ): ConfigurationProfile[] {
    return predictorModelStateModel.configurationProfiles;
  }

  @Action(PredictorModelActions.ResetToDefaults)
  resetToDefaults(ctx: StateContext<PredictorModelStateModel>) {
    const resetDefaults: PredictorModelStateModel = {
      ...stateDefaults,
      studyAreas: ctx.getState().studyAreas,
      configurationProfiles: ctx.getState().configurationProfiles,
    };
    ctx.setState(resetDefaults);
  }

  @Action(PredictorModelActions.OpenStudyAreaDropdown)
  openStudyAreaDropdown(ctx: StateContext<PredictorModelStateModel>) {
    ctx.patchState({
      showStudyAreaDropDown: true,
    });
  }

  @Action(PredictorModelActions.GetStudyAreas)
  getStudyAreas(ctx: StateContext<PredictorModelStateModel>) {
    return this.terrainDataService.getStudyAreas(true).pipe(
      tap((result: { $id: number; $values: StudyArea[] }) => {
        const studyAreas: StudyArea[] = result.$values;
        studyAreas.sort(this.StudyAreaSort);
        ctx.setState(
          patch({
            studyAreas: studyAreas,
          })
        );
      })
    );
  }

  @Selector()
  static terrainState(
    predictorModelStateModel: PredictorModelStateModel
  ): string {
    return predictorModelStateModel.terrainState;
  }

  @Action(PredictorModelActions.SetTerrainState, { cancelUncompleted: true })
  setTerrainState(
    ctx: StateContext<PredictorModelStateModel>,
    { state, status }: PredictorModelActions.SetTerrainState
  ) {
    ctx.patchState({
      terrainState: `${state} ${status.valueOf()}`,
      terrainRenderingStatus: status,
    });
  }

  @Action(PredictorModelActions.UnselectStudyArea)
  unselectStudyArea(ctx: StateContext<PredictorModelStateModel>) {
    ctx.patchState({
      showDetails: false,
      showCanvas: false,
      terrainLoadingStatus: TerrainLoadingStatus.None,
      selectedStudyAreaData: null,
      terrainState: '',
      terrainRenderingStatus: TerrainRenderingStatus.None,
      shapeFiles: [],
      selectedShapefile: undefined,
      selectedSimulationId: undefined,
      demFileUploading: false,
      demFileUploadStatus: 0,
      scenarios: [],
    });
    this.simulationService.reset();
    this.simulationLoopService.reset();
    this.canvasService.clearCanvas();
  }

  @Action(PredictorModelActions.SetSelectedStudyArea, {
    cancelUncompleted: true,
  })
  setSelectedStudyArea(
    ctx: StateContext<PredictorModelStateModel>,
    { id, name, licenseNumber }: PredictorModelActions.SetSelectedStudyArea
  ) {
    // setup the view
    ctx.patchState({
      selectedStudyAreaData: {
        error: null,
        name: name,
        id: id,
        shape: null,
        simulationId: null,
        terrainId: null,
        licenseNumber: licenseNumber
      },
    });

    // fetch the important details
    return this.terrainDataService.getStudyArea(id).pipe(
      mergeMap((result: IGeometryGeoJSON) =>
        this.terrainDataService
          .getSimulationEnvironment(id)
          .pipe(map((value) => ({ result, value })))
      ),
      toasty(PredictorModelActions.SetSelectedStudyArea, ctx.dispatch),
      tap(({ result, value }) => {
        const validsimulationId: boolean = value.simulationId != null;

        ctx.patchState({
          selectedStudyAreaData: {
            error: null,
            name: name,
            id: id,
            shape: result,
            simulationId: value.simulationId,
            terrainId: value.terrainId,
            licenseNumber: licenseNumber,
          },
          selectedSimulationId: value.simulationId,
          studyAreaAgentSettings: value.agentSettings,
          demFileUploading: false,
          demFileUploadStatus: 100,
          terrainLoadingStatus: validsimulationId
            ? TerrainLoadingStatus.Uploading
            : TerrainLoadingStatus.None,
        });
      }),
      catchError(() => {
        const studyAreaData: SelectedStudyAreaModel | null =
          ctx.getState().selectedStudyAreaData;
        ctx.patchState({
          selectedStudyAreaData: {
            ...studyAreaData!,
            error: 'Failed to load study area data. Please contact support.',
          },
        });
        return EMPTY;
      })
    );
  }

  @Action(PredictorModelActions.ResetAgentSettings)
  resetAgentSettings(ctx: StateContext<PredictorModelStateModel>) {
    ctx.patchState({
      currentAgentSettings: { ...stateDefaults.currentAgentSettings },
    });
  }

  @Action(PredictorModelActions.UpdateAgentSettings, {
    cancelUncompleted: true,
  })
  updateAgentSettings(
    ctx: StateContext<PredictorModelStateModel>,
    { agentSettings, simulationId }: PredictorModelActions.UpdateAgentSettings
  ) {
    return this.terrainDataService
      .setAgentSettings(simulationId, agentSettings)
      .pipe(toasty(PredictorModelActions.UpdateAgentSettings, ctx.dispatch));
  }

  @Action(PredictorModelActions.GetConfigurationProfiles, {
    cancelUncompleted: true,
  })
  getConfigurationProfiles(ctx: StateContext<PredictorModelStateModel>) {
    return this.configurationProfileService.get().pipe(
      tap((configurationProfiles) => {
        const sortedConfigurationProfiles: ConfigurationProfile[] =
          configurationProfiles.sort(
            (a, b) => Number(b.isGlobal) - Number(a.isGlobal)
          );
        ctx.patchState({
          configurationProfiles: sortedConfigurationProfiles,
        });
      })
    );
  }

  @Action(PredictorModelActions.CreateConfigurationProfile, {
    cancelUncompleted: true,
  })
  createConfigurationProfile(
    ctx: StateContext<PredictorModelStateModel>,
    { configurationProfile }: PredictorModelActions.CreateConfigurationProfile
  ) {
    return this.configurationProfileService
      .create(configurationProfile)
      .pipe(
        toasty(PredictorModelActions.CreateConfigurationProfile, ctx.dispatch)
      )
      .pipe(
        tap((profile) => {
          ctx.setState(
            patch({
              configurationProfiles: append([profile]),
            })
          );
        })
      );
  }

  @Action(PredictorModelActions.SetDetailVisibility)
  setDetailVisibility(
    ctx: StateContext<PredictorModelStateModel>,
    { showDetails }: PredictorModelActions.SetDetailVisibility
  ) {
    ctx.patchState({
      showDetails,
    });
  }

  @Action(PredictorModelActions.SetTerrainLoadingStatus)
  setTerrainLoadingStatus(
    ctx: StateContext<PredictorModelStateModel>,
    { terrainLoadingStatus }: PredictorModelActions.SetTerrainLoadingStatus
  ) {
    ctx.patchState({
      terrainLoadingStatus,
    });
  }

  @Action(PredictorModelActions.SetCanvasVisibility)
  setCanvasVisibility(
    ctx: StateContext<PredictorModelStateModel>,
    { showCanvas }: PredictorModelActions.SetCanvasVisibility
  ) {
    ctx.patchState({
      showCanvas,
    });
  }

  @Action(PredictorModelActions.AddShapeFile)
  addShapeFile(
    ctx: StateContext<PredictorModelStateModel>,
    { shapeFile }: PredictorModelActions.AddShapeFile
  ) {
    ctx.setState(
      patch({
        shapeFiles: append<CanvasLayer>([shapeFile]),
      })
    );
  }

  @Action(PredictorModelActions.DeleteShapeFile)
  deleteShapeFile(
    ctx: StateContext<PredictorModelStateModel>,
    { shapeFileToRemove }: PredictorModelActions.DeleteShapeFile
  ) {
    ctx.setState(
      patch({
        shapeFiles: removeItem<CanvasLayer>(
          (shapeFile) => shapeFile === shapeFileToRemove
        ),
      })
    );
  }

  @Action(PredictorModelActions.UpdateShapeFiles)
  updateShapeFiles(
    ctx: StateContext<PredictorModelStateModel>,
    { shapeFiles }: PredictorModelActions.UpdateShapeFiles
  ) {
    ctx.patchState({
      shapeFiles: shapeFiles,
    });
  }

  @Action(PredictorModelActions.AddFishnetShapeFileName)
  addFishnetShapeFileName(
    ctx: StateContext<PredictorModelStateModel>,
    { fileName }: PredictorModelActions.AddFishnetShapeFileName
  ) {
    ctx.patchState({
      fishnetShapeFileName: fileName,
    });
  }

  @Action(PredictorModelActions.SetSelectedShapeFile)
  setSelectedShapeFile(
    ctx: StateContext<PredictorModelStateModel>,
    { shapefile }: PredictorModelActions.SetSelectedShapeFile
  ) {
    ctx.patchState({
      selectedShapefile: shapefile,
    });
  }

  @Action(PredictorModelActions.AddScenario)
  addScenario(
    ctx: StateContext<PredictorModelStateModel>,
    { scenario }: PredictorModelActions.AddScenario
  ) {
    ctx.setState(
      patch({
        scenarios: append<CanvasLayer>([scenario]),
      })
    );
  }

  @Action(PredictorModelActions.UpdateScenarios)
  updateScenarios(
    ctx: StateContext<PredictorModelStateModel>,
    { scenarios }: PredictorModelActions.UpdateScenarios
  ) {
    ctx.patchState({
      scenarios: scenarios,
    });
  }

  @Action(PredictorModelActions.DeleteScenario)
  deleteScenario(
    ctx: StateContext<PredictorModelStateModel>,
    { scenarioToRemove }: PredictorModelActions.DeleteScenario
  ) {
    ctx.setState(
      patch({
        scenarios: removeItem<CanvasLayer>(
          (scenario) => scenario === scenarioToRemove
        ),
      })
    );
  }

  @Action(PredictorModelActions.SetInspectDetails)
  setInspectDetails(
    ctx: StateContext<PredictorModelStateModel>,
    { inspectDetails }: PredictorModelActions.SetInspectDetails
  ) {
    ctx.patchState({
      inspectDetails,
    });
  }

  @Action(PredictorModelActions.UploadDEMFile, { cancelUncompleted: true })
  uploadDEMFile(
    ctx: StateContext<PredictorModelStateModel>,
    { file }: PredictorModelActions.UploadDEMFile
  ) {
    const sa: SelectedStudyAreaModel | null =
      ctx.getState().selectedStudyAreaData;
    if (sa == null || sa.name == null) {
      console.error('Selected study area is null or has no name.');
      return;
    }

    // set status to uploading
    ctx.patchState({
      demFileUploading: true,
      demFileUploadStatus: 0,
    });

    // clear any existing simulation data
    if (this.terrainDataService.terrainGrid) {
      // side effect is making a delayed request to delete simulation
      this.simulationService.reset();
    }

    return this.fileUploadService.uploadDem(file, sa.name, sa.licenseNumber).pipe(
      toastyReport(PredictorModelActions.UploadDEMFile, ctx.dispatch),
      tap((event) => {
        if (event.type === HttpEventType.UploadProgress) {
          const percentDone: number = Math.round(
            (100 * event.loaded) / event.total
          );

          ctx.patchState({
            demFileUploadStatus: percentDone,
          });
        } else if (event.type === HttpEventType.Response) {
          const terrainId: string = event.body.terrainId;

          asapScheduler.schedule(() => {
            ctx.dispatch(new PredictorModelActions.ResetAgentSettings());
          });

          if (event.body.statusUri) {
            asapScheduler.schedule(() => {
              ctx.dispatch(
                new PredictorModelActions.TerrianFileUploadCheck(
                  terrainId,
                  true
                )
              );
            });
          } else {
            const currentStudyArea: SelectedStudyAreaModel =
              ctx.getState().selectedStudyAreaData!;
            currentStudyArea.status = event.body.statusUri
              ? TerrainLoadingStatus.Calculating
              : TerrainLoadingStatus.Done;

            ctx.patchState({
              // selectedSimulationId: simulationId,
              selectedStudyAreaData: currentStudyArea,
            });
          }
        }
      }),
      catchError((error) => {
        this.handleDEMUploadError(error, ctx);
        return EMPTY;
      })
    );
  }

  private handleDEMUploadError(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    error: any,
    ctx: StateContext<PredictorModelStateModel>
  ) {
    console.error('Error occurred:', error.message);
    ctx.patchState({
      demFileUploading: false,
      terrainLoadingStatus: TerrainLoadingStatus.FailedOnUpload,
    });

    if (error.status === 400 && typeof error.error === 'string') {
      asapScheduler.schedule(() => {
        ctx.dispatch(
          new NotificationsActions.Toast(error.error, Severity.Error)
        );
      });
    }
  }

  @Action(PredictorModelActions.TerrianFileUploadComplete)
  terrianFileUploadComplete(ctx: StateContext<PredictorModelStateModel>) {
    // we have completed the file upload
    // we are still in processes because the study shape needs to be updated
    // fetching the study area will fetch the terrain data
    ctx.patchState({
      demFileUploading: false,
      terrainLoadingStatus: TerrainLoadingStatus.Calculating,
    });

    asapScheduler.schedule(() => {
      ctx.dispatch(
        new PredictorModelActions.SetSelectedStudyArea(
          ctx.getState().selectedStudyAreaData!.id,
          ctx.getState().selectedStudyAreaData!.name,
          ctx.getState().selectedStudyAreaData!.licenseNumber
        )
      );
    });
  }

  @Action(PredictorModelActions.TerrianFileUploadCheck)
  terrianFileUploadCheck(
    ctx: StateContext<PredictorModelStateModel>,
    { terrainId, poll }: PredictorModelActions.TerrianFileUploadCheck
  ) {
    return this.terrainDataService.getTerrainStatus(terrainId).pipe(
      catchError((error) => {
        console.error('Error occurred:', error.message);
        ctx.dispatch(
          new PredictorModelActions.SetTerrainLoadingStatus(
            TerrainLoadingStatus.FailedOnCheckingStatus
          )
        );
        return of(
          new TiffProcessingStatus(AzFunctionRuntimeStatus.Unknown, true)
        );
      }),
      tap((response) => {
        if (response.status === AzFunctionRuntimeStatus.Completed) {
          ctx.dispatch(
            new PredictorModelActions.TerrianFileUploadComplete(terrainId)
          );
        } else if (
          (response.status === AzFunctionRuntimeStatus.Running ||
            response.status === AzFunctionRuntimeStatus.Pending) &&
          poll
        ) {
          setTimeout(
            () =>
              ctx.dispatch(
                new PredictorModelActions.TerrianFileUploadCheck(
                  terrainId,
                  true
                )
              ),
            500
          );
        } else {
          ctx.dispatch(
            new PredictorModelActions.SetTerrainLoadingStatus(
              TerrainLoadingStatus.FailedOnCheckingStatus
            )
          );

          const toastMessage: string =
            response.message || 'Failed to load terrain data.';
          ctx.dispatch(
            new NotificationsActions.Toast(toastMessage, Severity.Error)
          );
        }
      })
    );
  }

  @Action(PredictorModelActions.SetInteractionType)
  setInteractionType(
    ctx: StateContext<PredictorModelStateModel>,
    { interactionType }: PredictorModelActions.SetInteractionType
  ) {
    ctx.patchState({
      interactionType,
    });
    this.canvasService.setCursor(interactionType);
  }

  StudyAreaSort = (a: StudyArea, b: StudyArea) => {
    if (a.global !== b.global) {
      return a.global ? -1 : 1;
    }
    return a.studyAreaName.localeCompare(b.studyAreaName);
  };
}
